// frontend/src/components/MyEventRegistrationsList.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import {
  CalendarIcon as Calendar,
  TimeIcon as Clock,
  BlogIcon as FileText,
  AlertCircleIcon as AlertCircle,
  CheckCircleIcon as CheckCircle2,
  LoaderIcon as Loader2,
  ArrowRightIcon as ArrowRight,
  LocationIcon as MapPin,
  TeamIcon as Users
} from './Icons';

const MyEventRegistrationsList = () => {
  const { user, API_URL } = useContext(AuthContext);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMyRegistrations = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!user || !user.token) {
          setError('Sie müssen angemeldet sein, um Ihre Anmeldungen zu sehen.');
          setLoading(false);
          return;
        }

        const config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
        const res = await axios.get(`${API_URL}/events/my-registrations`, config);
        setMyRegistrations(res.data);
      } catch (err) {
        console.error('Fehler beim Abrufen Ihrer Anmeldungen:', err.response ? err.response.data.message : err.message);
        setError('Ihre Anmeldungen konnten nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    fetchMyRegistrations();
  }, [user, API_URL]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-surface-onVariant gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
        <p className="font-bold tracking-widest uppercase text-xs opacity-60">Lade Deine Anmeldungen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="md3-card-filled !bg-error-container/20 p-8 border border-error/10 flex items-center gap-4 text-error animate-scale-in">
        <AlertCircle size={32} />
        <p className="font-bold">{error}</p>
      </div>
    );
  }

  if (myRegistrations.length === 0) {
    return (
      <div className="text-center py-20 px-6">
        <div className="w-20 h-20 rounded-full bg-surface-containerHighest flex items-center justify-center text-surface-onVariant/30 mx-auto mb-6">
          <FileText size={40} />
        </div>
        <h3 className="text-headline-small font-bold text-surface-on mb-2">Noch keine Anmeldungen</h3>
        <p className="text-body-large text-surface-onVariant opacity-60 max-w-sm mx-auto">
          Du hast dich bisher noch für kein Event angemeldet. Schau doch mal in den Veranstaltungskalender!
        </p>
        <button className="md3-btn-tonal mt-8 rounded-full px-8 py-3 font-bold">
          Events entdecken
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-headline-small font-bold text-surface-on mb-8 flex items-center gap-3">
        <CheckCircle2 className="text-primary" />
        Deine angemeldeten Events
      </h3>

      <div className="grid gap-6">
        {myRegistrations.map((reg) => (
          <div
            key={reg._id}
            className="group relative md3-card-outlined !bg-surface-containerLowest hover:!bg-surface-containerLow transition-all duration-medium3 p-6 sm:p-8 !rounded-3xl border border-outlineVariant/50 flex flex-col md:flex-row gap-8 items-start md:items-center hover:shadow-elevation-2 active:scale-[0.99]"
          >
            {/* Event Icon/Date Box */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-primary/5 text-primary flex flex-col items-center justify-center border border-primary/10 shrink-0 group-hover:scale-110 transition-transform duration-emphasized">
              {reg.eventDetails?.date ? (
                <>
                  <span className="text-display-small font-black leading-none">
                    {new Date(reg.eventDetails.date).getDate()}
                  </span>
                  <span className="text-label-small font-black uppercase tracking-tighter opacity-60">
                    {new Date(reg.eventDetails.date).toLocaleString('de-DE', { month: 'short' })}
                  </span>
                </>
              ) : (
                <Calendar size={32} />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {reg.eventDetails ? (
                <div className="space-y-2">
                  <h4 className="text-headline-small font-bold text-surface-on truncate group-hover:text-primary transition-colors">
                    {reg.eventDetails.title}
                  </h4>
                  <div className="flex flex-wrap gap-x-6 gap-y-3">
                    <div className="flex items-center gap-2 text-surface-onVariant font-medium opacity-80">
                      <Clock size={16} />
                      <span className="text-sm">Angemeldet am {new Date(reg.registrationDate).toLocaleDateString('de-DE')}</span>
                    </div>
                    {reg.eventDetails.location && (
                      <div className="flex items-center gap-2 text-surface-onVariant font-medium opacity-80">
                        <MapPin size={16} />
                        <span className="text-sm">{reg.eventDetails.location}</span>
                      </div>
                    )}
                    {reg.participantType && (
                      <div className="flex items-center gap-2 text-primary font-bold">
                        <Users size={16} />
                        <span className="text-xs uppercase tracking-widest">{reg.participantType}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-body-medium text-surface-onVariant opacity-60 line-clamp-2 mt-3 leading-relaxed">
                    {reg.eventDetails.description}
                  </p>
                  {reg.additionalData?.children && Array.isArray(reg.additionalData.children) && reg.additionalData.children.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-outlineVariant/30">
                      <div className="text-xs font-bold text-surface-onVariant uppercase tracking-widest opacity-60 mb-2">Angemeldete Kinder</div>
                      <div className="flex flex-wrap gap-2">
                        {reg.additionalData.children.map((child, idx) => (
                          <div key={idx} className="bg-surface-containerHigh px-3 py-1.5 rounded-xl border border-outlineVariant/50 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                            <span className="text-sm font-bold text-surface-on">{child.name}</span>
                            {child.age && <span className="text-xs text-surface-onVariant opacity-60">({child.age} J.)</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-error-container/10 border border-error/20 rounded-2xl">
                  <p className="text-error font-bold flex items-center gap-2 italic">
                    <AlertCircle size={18} />
                    Termin-Details konnten nicht geladen werden (ID: {reg.eventId})
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4 w-full md:w-auto self-stretch">
              <div className="h-full w-px bg-outlineVariant mx-2 hidden md:block opacity-30" />
              <button className="flex-1 md:flex-none md3-btn-tonal !rounded-2xl h-14 px-8 gap-3 font-bold group-hover:bg-primary-container transition-all">
                Details
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyEventRegistrationsList;
